import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
const toast = { success: (m) => console.log("[toast]", m), error: (m) => console.error("[toast]", m), info: (m) => console.log("[toast]", m) };
import PageContainer, { PageHeader } from "@/components/PageContainer";
import api from "@/lib/assessments";
import { readAll } from "@/lib/assessments";
const formatApiError = (e) => (e && e.message) || "Unknown error";
import { Save, ChevronLeft, CheckCircle2, Circle } from "lucide-react";
import { DISCHARGE_ITEMS } from "@/data/instruments";

export default function DischargeReadiness() {
  const navigate = useNavigate();
  const [patient, setPatient] = useState("");
  const [notes, setNotes] = useState("");
  const [checked, setChecked] = useState({});
  const [saving, setSaving] = useState(false);

  const done = DISCHARGE_ITEMS.filter((i) => checked[i.id]).length;
  const pct = Math.round((done / DISCHARGE_ITEMS.length) * 100);
  let interp;
  if (pct >= 90) interp = { band: "Ready for discharge", detail: "All non-negotiables met. Confirm warm handoff and issue discharge summary." };
  else if (pct >= 70) interp = { band: "Nearly ready", detail: "Close out the remaining items before discharge." };
  else if (pct >= 50) interp = { band: "Not yet", detail: "Multiple non-negotiables outstanding. Extend LOS or step down carefully." };
  else interp = { band: "High discharge risk", detail: "Do not discharge until at least 70% of items are green. Discuss in supervision." };

  const missing = DISCHARGE_ITEMS.filter((i) => !checked[i.id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/assessments", {
        kind: "discharge_readiness",
        patient_identifier: patient || null,
        responses: { checked, missing: missing.map((m) => m.id) },
        score: done,
        interpretation: interp.band,
        notes: notes || null,
      });
      toast.success("Discharge readiness saved.");
      navigate("/portal/continuum/tools/history");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer testid="discharge-page">
      <Link to="/portal/continuum/tools" className="text-sm text-ink-muted hover:text-navy inline-flex items-center gap-1 mb-4" data-testid="discharge-back">
        <ChevronLeft size={14} strokeWidth={2} /> All tools
      </Link>

      <PageHeader overline="Portal · Tool" title="Discharge Readiness Checker"
        subtitle="Twelve non-negotiables that must be true before a patient steps down or discharges from your program." />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="card p-5">
            <label className="label">Patient identifier</label>
            <input className="field" value={patient} onChange={(e) => setPatient(e.target.value)} placeholder="Initials or MRN" data-testid="discharge-patient" />
          </div>

          <div className="card p-5">
            <ol className="space-y-2">
              {DISCHARGE_ITEMS.map((it) => (
                <li key={it.id} data-testid={`discharge-item-${it.id}`}>
                  <button
                    type="button"
                    onClick={() => setChecked({ ...checked, [it.id]: !checked[it.id] })}
                    className={`w-full text-left flex items-start gap-3 p-3 rounded-md border transition-colors ${
                      checked[it.id] ? "bg-teal-light/30 border-teal/40" : "bg-surface border-line-soft hover:border-line"
                    }`}
                  >
                    {checked[it.id] ? (
                      <CheckCircle2 size={18} strokeWidth={2} className="text-teal flex-shrink-0 mt-0.5" />
                    ) : (
                      <Circle size={18} strokeWidth={1.5} className="text-ink-faint flex-shrink-0 mt-0.5" />
                    )}
                    <span className={`text-sm ${checked[it.id] ? "text-navy" : "text-ink"}`}>{it.text}</span>
                  </button>
                </li>
              ))}
            </ol>
          </div>

          <div className="card p-5">
            <label className="label">Notes</label>
            <textarea className="field min-h-[80px]" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="discharge-notes" />
          </div>
        </div>

        <aside className="lg:sticky lg:top-6 self-start">
          <div className="card p-6" data-testid="discharge-summary">
            <div className="overline mb-2">Readiness</div>
            <div className="text-5xl font-semibold text-navy">{pct}%</div>
            <div className="text-sm text-ink-muted mt-1">{done} of {DISCHARGE_ITEMS.length} items complete</div>

            <div className="mt-4">
              <div className={`badge ${pct >= 70 ? "badge-teal" : "badge-rust"} mb-2`}>{interp.band}</div>
              <p className="text-sm text-ink leading-relaxed">{interp.detail}</p>
            </div>

            {missing.length > 0 && (
              <>
                <div className="rule my-4" />
                <div className="text-xs uppercase tracking-wider text-rust mb-2">Outstanding</div>
                <ul className="text-sm space-y-1">
                  {missing.slice(0, 6).map((m) => (
                    <li key={m.id} className="text-ink text-xs">· {m.text}</li>
                  ))}
                  {missing.length > 6 && <li className="text-xs text-ink-muted">+ {missing.length - 6} more</li>}
                </ul>
              </>
            )}

            <div className="rule my-4" />
            <button onClick={save} disabled={saving} className="btn btn-primary w-full justify-center" data-testid="discharge-save">
              {saving ? "Saving..." : (<><Save size={14} strokeWidth={2} /> Save to history</>)}
            </button>
          </div>
        </aside>
      </div>
    </PageContainer>
  );
}
